import { useMemo, useRef } from 'react';
import { useCalculator } from '../state/CalculatorProvider';
import { getAllFields, getFieldsErrors } from '../state/reducer';

/**
 * The recompute rule (docs/calculator-engine-architecture.md §4.5):
 * calculate() runs whenever a value changes AND (recompute === 'live' OR
 * results are currently visible). It must NOT run while results are
 * hidden in 'onComplete' mode — the legacy HW Index scores on every
 * keystroke via a $watch and it is measurably wasteful.
 *
 * With config.invalidInput === 'hold' (HSA Savings, 2026-10-08), a field
 * whose current value fails validation is calculated with its last VALID
 * value instead, so the chart and totals never show figures for an
 * out-of-range input while its error message is up -- prod's legacy
 * behavior. The view still gets the raw values (what's in the fields).
 */
export function ResultsFrame() {
  const { config, state, limits, setValue, goToStep, reset, fmt } = useCalculator();

  const shouldCompute = config.recompute === 'live' || state.phase === 'results';

  const lastValid = useRef(state.values);
  const calcValues = useMemo(() => {
    if (config.invalidInput !== 'hold') return state.values;
    const errors = getFieldsErrors(getAllFields(config), state.values, { limits, values: state.values });
    const merged = { ...state.values };
    for (const name of Object.keys(errors) as (keyof typeof merged)[]) {
      merged[name] = lastValid.current[name];
    }
    // Idempotent for the same input (safe under StrictMode's double render).
    lastValid.current = merged;
    return merged;
  }, [config, state.values, limits]);

  const result = useMemo(() => {
    if (!shouldCompute) return null;
    return config.calculate(calcValues, limits);
  }, [shouldCompute, calcValues, limits, config]);

  if (result === null) return null;

  const ResultsView = config.ResultsView;
  return (
    <ResultsView
      result={result}
      values={state.values}
      limits={limits}
      setValue={setValue}
      goToStep={goToStep}
      reset={reset}
      fmt={fmt}
    />
  );
}
